import { CellSelectionOptions, ColDef, ValueFormatterParams, ValueParserParams } from 'ag-grid-community';
import { formatIsoDateValue } from '../../utils/date-format.util';
import {
    ON_REQUEST_PRICE_VALUE,
    PRICE_FIELD_NAMES,
    WorkflowConfigField,
    WorkflowConfigFieldInput,
    WorkflowConfigFieldInputType,
    WorkflowConfigFieldSelect,
    WorkflowConfigFieldType,
    formatGermanDecimalValue,
    getEffectiveInputType,
    isNumericInputType,
    parseGermanDecimalValue
} from '@monorepo/interfaces';

export const MAX_PRODUCTS = 50;

export const DEFAULT_COL_DEF: ColDef = {
    minWidth: 150,
    resizable: true,
    sortable: false,
    filter: false,
    enableCellChangeFlash: true
};

export const CORE_MLFB_COL_DEF: ColDef = {
    headerName: 'Core MLFB',
    field: 'coreMlfb',
    pinned: 'left',
    lockPinned: true,
    lockPosition: 'left',
    editable: false,
    suppressFillHandle: true,
    minWidth: 150,
    cellClass: 'font-semibold'
};

export const FILL_HANDLE_CELL_SELECTION: CellSelectionOptions = {
    handle: {
        mode: 'fill',
        direction: 'y'
    }
};

export function getCellEditorConfig(field: WorkflowConfigField): Partial<ColDef> {
    const inputType =
        field.fieldType === WorkflowConfigFieldType.INPUT
            ? getEffectiveInputType(field as WorkflowConfigFieldInput)
            : undefined;

    switch (field.fieldType) {
        case WorkflowConfigFieldType.INPUT: {
            if (isNumericInputType(inputType)) {
                return {
                    cellEditor: 'agTextCellEditor',
                    cellEditorParams: { useFormatter: true },
                    valueFormatter: (params: ValueFormatterParams) => {
                        if (params.value == null || params.value === '') return '';
                        return formatGermanDecimalValue(params.value);
                    },
                    valueParser: (params: ValueParserParams) => {
                        if (PRICE_FIELD_NAMES.includes(field.name) && params.newValue === ON_REQUEST_PRICE_VALUE) {
                            return ON_REQUEST_PRICE_VALUE;
                        }
                        return parseGermanDecimalValue(params.newValue) ?? params.oldValue ?? '';
                    }
                };
            }
            switch (inputType) {
                case WorkflowConfigFieldInputType.BOOLEAN:
                    return {
                        cellRenderer: 'agCheckboxCellRenderer',
                        cellEditor: 'agCheckboxCellEditor',
                        cellDataType: 'boolean'
                    };
                case WorkflowConfigFieldInputType.DATE:
                    return {
                        //  disable ag-grid date inference; raw values arrive as ISO datetime
                        // (2003-01-01T00:00:00.000Z) and the editor emits YYYY-MM-DD, which the inferred
                        // 'dateTimeString' parser rejects → edits silently dropped.
                        cellDataType: false,
                        cellEditor: 'siDateCellEditor',
                        valueFormatter: formatIsoDateValue
                    };
                default:
                    return {
                        cellEditor: 'agTextCellEditor'
                    };
            }
        }
        case WorkflowConfigFieldType.SELECT: {
            const selectField = field as WorkflowConfigFieldSelect;
            return {
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: (_params: unknown) => ({
                    values: selectField.options || [],
                    allowTyping: true,
                    filterList: true,
                    highlightMatch: true,
                    searchType: 'matchAny',
                    valueListMaxHeight: 150
                }),
                cellEditorPopup: true
            };
        }
        // DATE_RANGE stores a single date boundary from the API; a dedicated range editor is not needed
        case WorkflowConfigFieldType.DATE_RANGE:
            return {
                //  see DATE case — disable date inference so YYYY-MM-DD edits commit
                cellDataType: false,
                cellEditor: 'siDateCellEditor',
                valueFormatter: formatIsoDateValue
            };
        default:
            return { cellEditor: 'agTextCellEditor' };
    }
}
